import { useEffect } from 'react';

// Intercom's own resting offset from the right edge of the viewport.
const DEFAULT_PADDING = 20;
const STYLE_ELEMENT_ID = 'intercom-launcher-offset';
const OFFSET_VARIABLE = '--intercom-launcher-offset';
const ACTIVE_CLASS = 'intercom-offset-active';
// Matches the offset transition below, with a margin so the glide back finishes first.
const DEACTIVATE_DELAY_MS = 300;

// The modern Intercom Messenger ignores the horizontal_padding setting, so the offset is applied
// with CSS instead. The rules cover both renderings: the lightweight launcher div shown before
// any interaction, and the full-app elements afterwards — the opened messenger window is the
// `div.intercom-messenger-frame` itself, while the launcher/notification iframes sit in wrappers
// with hashed class names that are matched structurally. `left: auto` is required because some
// variants are positioned by a computed `left`, which makes `right` ignored. The messenger
// declares its own transition list (without `right`), so the offset would jump instead of glide;
// the marker class takes over the transition with !important only while a drawer is open. Below
// the tablet breakpoint the drawer covers the viewport anyway, so the launcher stays put.
const INTERCOM_SELECTORS = [
  '.intercom-lightweight-app-launcher',
  'div.intercom-messenger-frame',
  '.intercom-app > div:has(> iframe.intercom-launcher-frame)',
  '.intercom-app > div:has(> iframe[name="intercom-notifications-frame"])'
];

const ensureStyleElement = () => {
  if (document.getElementById(STYLE_ELEMENT_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = `
    @media (min-width: 768px) {
      ${INTERCOM_SELECTORS.join(',\n      ')} {
        left: auto !important;
        right: calc(${DEFAULT_PADDING}px + var(${OFFSET_VARIABLE}, 0px)) !important;
      }
      ${INTERCOM_SELECTORS.map((selector) => `html.${ACTIVE_CLASS} ${selector}`).join(
        ',\n      '
      )} {
        transition: right 225ms ease-in-out !important;
      }
    }`;
  document.head.appendChild(style);
};

let deactivateTimer: number | undefined;

const activateOffset = () => {
  window.clearTimeout(deactivateTimer);
  document.documentElement.classList.add(ACTIVE_CLASS);
};

const deactivateOffset = () => {
  window.clearTimeout(deactivateTimer);
  // Keep the transition takeover until the glide back to the default position has finished.
  deactivateTimer = window.setTimeout(() => {
    document.documentElement.classList.remove(ACTIVE_CLASS);
  }, DEACTIVATE_DELAY_MS);
};

/**
 * Keeps the Intercom launcher (and an opened messenger window) left of a right-anchored drawer
 * while `active`, so the drawer does not cover it. Pass an element rendered inside the drawer
 * from a callback ref (state), NOT a ref object — the drawer's portal content mounts after the
 * open render, and only a state-held node re-runs the effect once it exists. The offset follows
 * the live width of the surrounding `.MuiDrawer-paper` (falling back to the element itself), so
 * it tracks responsive drawer widths and window resizes, and the launcher returns to its default
 * position when the drawer closes.
 */
export const useIntercomHorizontalPadding = (active: boolean, anchor: HTMLElement | null) => {
  useEffect(() => {
    if (!active || !anchor) {
      return;
    }

    ensureStyleElement();
    activateOffset();
    const element = anchor.closest<HTMLElement>('.MuiDrawer-paper') ?? anchor;
    const applyOffset = () => {
      document.documentElement.style.setProperty(
        OFFSET_VARIABLE,
        `${Math.round(element.getBoundingClientRect().width)}px`
      );
    };

    applyOffset();
    const observer = new ResizeObserver(applyOffset);
    observer.observe(element);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(OFFSET_VARIABLE);
      deactivateOffset();
    };
  }, [active, anchor]);
};
